import React, { useCallback, useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Popover,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

import { DayPicker, DateRange } from 'react-day-picker';
import { colors } from 'design-system';

// helpers function date
import { format, parse, isBefore, isAfter, isValid } from 'date-fns';

// locale
import es from 'date-fns/locale/es';

// date picker component original styles
import 'react-day-picker/dist/style.css';

// icons
import { CalendarIcon } from '../Icons';

// local components
import { Caption, Footer, HeaderInputDate } from './components';

const MIN_FROM_DATE = new Date(2020, 1, 1);
const MAX_TO_DATE = new Date();

const FORMAT_DATE_INPUTS = 'dd/MM/yyyy';
export const FORMART_DATE_TAG_BUTTON = 'MMM dd, yyyy';

const useStyles = makeStyles(() =>
  createStyles({
    containerButtonOpenDatePicker: {
      width: 'max-content',
      '&:hover': {
        background: colors.cloud,
      },
    },
    startIcon: {
      marginRight: '4px',
    },
    buttonDay: {
      '&:disabled': {
        opacity: 0.25,
      },
      '&:hover': {
        background: '#E0F8FF',
        cursor: 'pointer',
      },
    },
    paper: { width: '340px', borderRadius: '10px' },
  }),
);

type DateRangePickerProps = {
  onAddFilter: (description: string, startDate: Date, endDate: Date) => void;
  onReset?: () => void;
  isSelected: boolean;
  // we need to store the button content in the parent component because the component will be unmounted and the content button will be deleted. if the user closes the drawer
  descriptionWhenSelected: string;
  descriptionWhenNotSelected: string;
  minFromDate?: Date;
  maxToDate?: Date;
  buttonClassName?: string;
  dateToShowFormat?: string;
  buttonStartIconColor?: string;
};

const DateRangePicker = ({
  onAddFilter,
  isSelected,
  onReset,
  descriptionWhenSelected,
  descriptionWhenNotSelected,
  buttonClassName,
  minFromDate = MIN_FROM_DATE,
  maxToDate = MAX_TO_DATE,
  dateToShowFormat = FORMART_DATE_TAG_BUTTON,
  buttonStartIconColor,
}: DateRangePickerProps): JSX.Element => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [range, setRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSearch = () => {
    const dateFormattedUsedToShow = `${format(
      range?.from as Date,
      dateToShowFormat,
    )} - ${format(range?.to as Date, dateToShowFormat)}`;

    // when search a date range, change button text with the range date selected
    // setdescriptionWhenSelected(dateFormattedUsedToShow);

    // search with date range selected and add tag to dashbord
    onAddFilter(
      dateFormattedUsedToShow,
      range?.from as Date,
      range?.to as Date,
    );

    // close the modal
    handleClose();
  };

  const handleReset = useCallback(() => {
    setFromValue('');
    setToValue('');
    setRange({ from: undefined, to: undefined });
  }, []);

  const handleRangeSelect = useCallback(
    (newRange: DateRange | undefined): void => {
      setRange(newRange);

      if (newRange?.from) {
        setFromValue(format(newRange.from, FORMAT_DATE_INPUTS));
      }
      if (newRange?.to) {
        setToValue(format(newRange.to, FORMAT_DATE_INPUTS));
      }
    },
    [],
  );

  const handleFromChangeInput = useCallback(
    (value: string): void => {
      const fromDate = parse(value, FORMAT_DATE_INPUTS, new Date());

      if (!isValid(fromDate)) {
        setFromValue('');
        return setRange({ from: undefined, to: range?.to });
      }

      if (isBefore(fromDate, MIN_FROM_DATE) || isAfter(fromDate, MAX_TO_DATE)) {
        return handleReset();
      }

      if (range?.to && isAfter(fromDate, range.to)) {
        return handleReset();
      }
      setFromValue(value);
      return setRange({ from: fromDate, to: range?.to });
    },
    [handleReset, range?.to],
  );

  const handleToChangeInput = useCallback(
    (value: string) => {
      const toDate = parse(value, FORMAT_DATE_INPUTS, new Date());

      if (!isValid(toDate)) {
        setToValue('');
        return setRange({ from: range?.from, to: undefined });
      }

      if (isBefore(toDate, MIN_FROM_DATE) || isAfter(toDate, MAX_TO_DATE)) {
        return handleReset();
      }

      if (range?.from && isBefore(toDate, range.from)) {
        return handleReset();
      }

      setToValue(value);
      return setRange({ from: range?.from, to: toDate });
    },
    [handleReset, range?.from],
  );

  // when select a different date optione reset date picker values values
  useEffect(() => {
    if (!isSelected) {
      handleReset();
    }
  }, [handleReset, isSelected]);

  return (
    <>
      <div className={classes.containerButtonOpenDatePicker}>
        <Button
          color="primary"
          id="sales-web-buton-open-range-date"
          className={buttonClassName}
          onClick={handleClick}
          classes={{
            startIcon: classes.startIcon,
          }}
          startIcon={<CalendarIcon color={buttonStartIconColor} />}
          data-testid="button-open-date-picker"
        >
          {isSelected ? descriptionWhenSelected : descriptionWhenNotSelected}
        </Button>
        {onReset && isSelected && (
          <IconButton
            style={{ backgroundColor: 'transparent' }}
            onClick={onReset}
            data-testid="set-to-default-button"
          >
            <CloseIcon
              style={{
                color: colors.bright_turquoise,
                width: '20px',
              }}
            />
          </IconButton>
        )}
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{ className: classes.paper }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        data-testid="popover"
      >
        <HeaderInputDate
          data-testid="header-with-inputs"
          handleFromChangeInput={handleFromChangeInput}
          handleToChangeInput={handleToChangeInput}
          fromValue={fromValue}
          toValue={toValue}
        />
        <DayPicker
          components={{
            Caption,
          }}
          data-testid="dayPicker"
          mode="range"
          locale={es}
          selected={range}
          onSelect={handleRangeSelect}
          fromDate={minFromDate}
          toDate={maxToDate}
          weekStartsOn={0}
          classNames={{
            button: classes.buttonDay,
          }}
          styles={{
            table: {
              borderCollapse: 'inherit',
            },
            month: {
              marginRight: 'auto',
              marginLeft: 'auto',
            },
          }}
          modifiersStyles={{
            range_start: {
              background: colors.assa_blue,
              borderColor: colors.assa_blue,
              borderRadius: '100%',
            },
            range_end: {
              background: colors.assa_blue,
              borderColor: colors.assa_blue,
              borderRadius: '100%',
            },
            range_middle: {
              background: colors.cloud,
              color: colors.abbey,
            },
          }}
        />
        <Footer
          disableAcceptButton={
            range?.from === undefined || range?.to === undefined
          }
          data-testid="footer"
          onAccept={handleSearch}
          onCancel={handleClose}
        />
      </Popover>
    </>
  );
};

export default DateRangePicker;
